// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toApiError } from "@/api/api-error";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type CutOffTimesSortBy = components["schemas"]["StoresCutOffTimesSortByEnum"];
export type ChainCutOffTimesSortBy = components["schemas"]["ChainsCutOffTimesSortByEnum"];

/**
 * A chain list has no Scope column, so its sort enum has no `scope` member. Drop it rather than
 * pass it through — a `sortId=scope` carried over in the URL from a store view would 422 otherwise.
 * Omitting it lets the endpoint's own default apply.
 */
export function toChainSortBy(sortBy: CutOffTimesSortBy | undefined): ChainCutOffTimesSortBy | undefined {
  return sortBy === "scope" ? undefined : sortBy;
}
export type CutOffTimeScope = components["schemas"]["StoresCutOffTimesScopeEnum"];
export type CutOffUseCase = components["schemas"]["CutOffUseCase"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

export type CutOffTimeEntry = components["schemas"]["StoresCutOffTimesListEntryResponse"];
export type CutOffTimeAddBody = components["schemas"]["StoresCutOffTimesAddRequest"];
export type CutOffTimeEditBody = components["schemas"]["StoresCutOffTimesEditRequest"];

export interface CutOffTimesListResult {
  cutOffTimes: CutOffTimeEntry[];
  count: number;
}

/**
 * Which slice of the estate a list is for. A store list returns the store's own rows plus the ones
 * it inherits from its chain; a chain list returns only the chain's own rows.
 */
export type CutOffTimesTarget = { kind: "store"; storeName: string } | { kind: "chain"; chainName: string };

interface CutOffTimesListParams {
  target: CutOffTimesTarget;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: CutOffTimesSortBy;
  orderBy?: SortOrder;
  /** Store lists only — a chain list is chain-scoped by definition. */
  scope?: CutOffTimeScope | "";
  useCase?: CutOffUseCase | "";
  enabled?: boolean;
}

export function useCutOffTimesQuery(params: CutOffTimesListParams) {
  const { target, page = 1, limit = 20, search, sortBy, orderBy, scope, useCase, enabled } = params;
  const targetKey = target.kind === "store" ? target.storeName : target.chainName;

  return useQuery({
    queryKey: ["cutOffTimes", "list", target.kind, targetKey, { page, limit, search, sortBy, orderBy, scope, useCase }],
    enabled,
    queryFn: async () => {
      const query = {
        page,
        limit,
        search: search || undefined,
        sortBy,
        orderBy,
        "filter[useCase]": useCase || undefined,
      };
      const res =
        target.kind === "store"
          ? await api.GET("/v1/stores/{store_name}/cut-off-times", {
              params: {
                path: { store_name: target.storeName },
                query: { ...query, "filter[scope]": scope || undefined },
              },
            })
          : await api.GET("/v1/chains/{chain_name}/cut-off-times", {
              params: {
                path: { chain_name: target.chainName },
                query: { ...query, sortBy: toChainSortBy(sortBy) },
              },
            });
      return res.data as unknown as CutOffTimesListResult;
    },
  });
}

/**
 * Refresh the lists a mutation just changed. A chain-scoped write also has to invalidate every
 * store list, because that row shows up as inherited on all of them.
 */
function invalidateCutOffTimes(queryClient: QueryClient, target: CutOffTimesTarget) {
  const targetKey = target.kind === "store" ? target.storeName : target.chainName;
  queryClient.invalidateQueries({ queryKey: ["cutOffTimes", "list", target.kind, targetKey] });
  if (target.kind === "chain") {
    queryClient.invalidateQueries({ queryKey: ["cutOffTimes", "list", "store"] });
  }
}

export function useCreateCutOffTimeMutation(target: CutOffTimesTarget) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CutOffTimeAddBody) => {
      const res =
        target.kind === "store"
          ? await api.POST("/v1/stores/{store_name}/cut-off-times", {
              params: { path: { store_name: target.storeName } },
              body,
            })
          : await api.POST("/v1/chains/{chain_name}/cut-off-times", {
              params: { path: { chain_name: target.chainName } },
              body,
            });
      // Carries the status through so the dialog can tell a 409 duplicate from anything else —
      // the global toast middleware only covers 422/5xx, so a swallowed 409 would be invisible.
      if (res.error) throw toApiError(res.error, res.response, "Failed to create cut off time");
      return res.data;
    },
    onSuccess: () => invalidateCutOffTimes(queryClient, target),
  });
}

export function useEditCutOffTimeMutation(target: CutOffTimesTarget) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CutOffTimeEditBody) => {
      const res =
        target.kind === "store"
          ? await api.PATCH("/v1/stores/{store_name}/cut-off-times", {
              params: { path: { store_name: target.storeName } },
              body,
            })
          : await api.PATCH("/v1/chains/{chain_name}/cut-off-times", {
              params: { path: { chain_name: target.chainName } },
              body,
            });
      if (res.error) throw toApiError(res.error, res.response, "Failed to update cut off time");
      return res.data;
    },
    onSuccess: () => invalidateCutOffTimes(queryClient, target),
  });
}

export function useDeleteCutOffTimeMutation(target: CutOffTimesTarget) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cutOffTimeId: number) => {
      const body = { cutOffTimeId };
      const res =
        target.kind === "store"
          ? await api.DELETE("/v1/stores/{store_name}/cut-off-times", {
              params: { path: { store_name: target.storeName } },
              body,
            })
          : await api.DELETE("/v1/chains/{chain_name}/cut-off-times", {
              params: { path: { chain_name: target.chainName } },
              body,
            });
      if (res.error) throw toApiError(res.error, res.response, "Failed to delete cut off time");
      return res.data;
    },
    onSuccess: () => invalidateCutOffTimes(queryClient, target),
  });
}
